import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useGetUserOrdersQuery, useGetProfileQuery, useGetStockSummaryQuery } from "@/store";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ShoppingCart,
  Package,
  Users,
  TrendingUp,
  DollarSign,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { LowStockAlert } from "@/components/admin";

export function AdminDashboardScreen() {
  const { data: ordersData, isLoading: ordersLoading } = useGetUserOrdersQuery();
  const { data: profileData } = useGetProfileQuery();
  const { data: stockSummaryData } = useGetStockSummaryQuery();
  const [greeting, setGreeting] = useState("")

  useEffect(() => {
    const hour = new Date().getHours()
    setGreeting(hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening")
  }, [])

  // Get admin's first name
  const adminFirstName = profileData?.data?.name?.split(" ")[0] || "Admin";

  const orders = ordersData?.data || [];
  const totalRevenue = orders
    .filter((order) => order.status === "DELIVERED")
    .reduce((sum, order) => sum + (order.total || 0), 0);
  const pendingOrders = orders.filter(
    (order) => order.status === "PENDING"
  ).length;

  // Stock alerts count
  const stockAlerts = (stockSummaryData?.data?.outOfStock || 0) + 
                      (stockSummaryData?.data?.criticalStock || 0) + 
                      (stockSummaryData?.data?.lowStock || 0);

  const stats = [
    {
      title: "Total Revenue",
      value: `₦${(totalRevenue).toLocaleString("en-NG", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      description: "All-time revenue",
      icon: DollarSign,
      trend: "+12.5%",
    },
    {
      title: "Total Orders",
      value: orders.length.toString(),
      description: "All orders",
      icon: ShoppingCart,
      trend: "+8.2%",
    },
    {
      title: "Pending Orders",
      value: pendingOrders.toString(),
      description: "Awaiting fulfillment",
      icon: Clock,
      trend: "+3.1%",
    },
    {
      title: "Stock Alerts",
      value: stockAlerts.toString(),
      description: "Products needing attention",
      icon: AlertTriangle,
      trend: stockAlerts > 0 ? "Action needed" : "All good",
      highlight: stockAlerts > 0,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{greeting}, {adminFirstName}!</h1>
        <p className="text-muted-foreground">
          Here's an overview of your business.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Card key={stat.title} className={(stat as any).highlight ? "border-amber-300 dark:border-amber-700" : ""}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {stat.title}
              </CardTitle>
              <stat.icon className={`h-4 w-4 ${(stat as any).highlight ? "text-amber-500" : "text-muted-foreground"}`} />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${(stat as any).highlight && Number(stat.value) > 0 ? "text-amber-600 dark:text-amber-400" : ""}`}>
                {stat.value}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {stat.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Recent Orders</CardTitle>
            <CardDescription>
              Latest orders from your customers
            </CardDescription>
          </CardHeader>
          <CardContent>
            {ordersLoading ? (
              <div className="text-center py-8 text-muted-foreground">
                Loading orders...
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No orders yet
              </div>
            ) : (
              <div className="space-y-4">
                {orders.slice(0, 5).map((order) => (
                    <div
                      key={order.id}
                      className="flex items-center justify-between border-b pb-3 last:border-0"
                    >
                      <div>
                        <p className="font-medium">Order #{order.displayOrderId}</p>
                        <p className="text-sm text-muted-foreground">
                          ₦{(order.total / 100).toLocaleString("en-NG", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} •{" "}
                          {new Date(order.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          order.status === "DELIVERED"
                            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                            : order.status === "PENDING"
                            ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                            : "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200"
                        }`}
                      >
                        {order.status.replace(/_/g, " ")}
                      </span>
                    </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stock Alerts Widget */}
        <LowStockAlert maxItems={5} showSummary={true} />
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common admin tasks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/admin/products/new">
              <button className="w-full text-left px-4 py-3 rounded-lg border hover:bg-accent transition-colors">
                <Package className="h-5 w-5 mb-2 text-primary" />
                <p className="font-medium text-sm">Add Product</p>
                <p className="text-xs text-muted-foreground">Create new product</p>
              </button>
            </Link>
            <Link to="/admin/orders">
              <button className="w-full text-left px-4 py-3 rounded-lg border hover:bg-accent transition-colors">
                <ShoppingCart className="h-5 w-5 mb-2 text-primary" />
                <p className="font-medium text-sm">View Orders</p>
                <p className="text-xs text-muted-foreground">Manage orders</p>
              </button>
            </Link>
            <Link to="/admin/users">
              <button className="w-full text-left px-4 py-3 rounded-lg border hover:bg-accent transition-colors">
                <Users className="h-5 w-5 mb-2 text-primary" />
                <p className="font-medium text-sm">Manage Admins</p>
                <p className="text-xs text-muted-foreground">User management</p>
              </button>
            </Link>
            <Link to="/admin/analytics">
              <button className="w-full text-left px-4 py-3 rounded-lg border hover:bg-accent transition-colors">
                <TrendingUp className="h-5 w-5 mb-2 text-primary" />
                <p className="font-medium text-sm">Analytics</p>
                <p className="text-xs text-muted-foreground">View reports</p>
              </button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

